import type { Session } from './SessionProvider';

/**
 * Pluggable session-persistence interface.
 *
 * The contract is deliberately minimal — the `SessionProvider` calls
 * `read` once at startup and `write` / `clear` on state transitions.
 * Real implementations live in the host apps because the "right"
 * backend is platform-specific:
 *
 *   - Mobile (Expo): `expo-secure-store` via Keychain / Keystore.
 *   - Web:            httpOnly cookie set by the auth endpoint (future).
 *                     `localStorage` is used only as a placeholder
 *                     until the backend lands.
 *
 * Implementations must tolerate "value not present" and "value
 * corrupt" as the same thing — return `null`. A throwing `read` would
 * trap the app in an error state on every cold start; a storage bug
 * is always less bad than a login loop.
 */
export interface SessionStorage {
  read(): Promise<Session | null>;
  write(session: Session): Promise<void>;
  clear(): Promise<void>;
}

/**
 * Default storage — in-memory only, loses state across reloads.
 * Used when no app-provided storage is wired up. Keeps the provider
 * API consistent so tests / Storybook don't have to mock anything.
 */
export function memorySessionStorage(): SessionStorage {
  let current: Session | null = null;
  return {
    async read() {
      return current;
    },
    async write(session) {
      current = session;
    },
    async clear() {
      current = null;
    },
  };
}
